import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import {
  SearchAndSort,
  SkeletonTable,
  EmptyState,
  ExportButton,
  Pagination,
} from "../../features/statsNew/components";
import { exportLearnersToCsv } from "../../features/statsNew/utils/exportCsv";
import type { DateRange } from "../../features/statsNew/components";
import { getSessionDate, isSessionInDateRange } from "../../features/statsNew/utils/dateUtils";
import parseSimplifiedUserIdentifier from "../../common/util/parseSimplifiedUserIdentifier";
import {
  getAllSessionsByProjectGroup,
  sessionsByProjectGroupSelector,
} from "../../features/stats/state/statsSlice";
import { AppDispatch } from "../../common/state/store";
import { setDateRange } from "../../features/statsNew/state/statsNewSlice";
import { useCreatorStatsCache } from "../../features/statsNew/hooks";

interface OutletContextType {
  dateRange: DateRange;
}

interface ApprenantData {
  id: number | string;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  sessionCount: number;
  lastSessionDate: Date | null;
  lastSessionScore: number | null;
  avatarUrl?: string;
  isAnonymous: boolean;
}

type SortColumn = "sessions" | "firstName" | "lastName" | "email" | "lastDate" | "lastScore";
type SortDirection = "asc" | "desc";

const ApprenantsListView = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { dateRange } = useOutletContext<OutletContextType>();
  const locale = i18n.language === "fr" ? fr : enUS;
  const { shouldRefetch, markAsFetched } = useCreatorStatsCache();

  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);

  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("lastDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedApprenants, setSelectedApprenants] = useState<Set<number | string>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Fetch sessions only if cache is invalid
  useEffect(() => {
    if (shouldRefetch) {
      setIsLoading(true);
      dispatch(getAllSessionsByProjectGroup({ componentId: "statsNewApprenantsListView" })).then(
        () => {
          markAsFetched();
          setIsLoading(false);
        },
      );
    }
  }, [dispatch, shouldRefetch, markAsFetched]);

  // Show loading state
  const showSkeleton = isLoading || (shouldRefetch && sessionsByProjectGroup.length === 0);

  const handleClearDateRange = () => {
    dispatch(setDateRange({ startDate: null, endDate: null }));
  };

  // Extract unique users from all sessions
  const apprenantsList = useMemo(() => {
    const userMap = new Map<string, ApprenantData>();

    sessionsByProjectGroup.forEach((group) => {
      group.projects.forEach((project) => {
        project.sessions.forEach((session) => {
          const sessionDate = getSessionDate(session);

          // Apply date range filter
          if (!isSessionInDateRange(sessionDate, dateRange)) return;

          // Determine user identity
          const isAnonymous = !session.enduser;
          const uniqueKey = isAnonymous ? "anon_all" : `user_${session.enduser?.id}`;

          const existing = userMap.get(uniqueKey);

          if (existing) {
            // Update existing user data
            existing.sessionCount++;
            if (!existing.lastSessionDate || sessionDate > existing.lastSessionDate) {
              existing.lastSessionDate = sessionDate;
              existing.lastSessionScore = session.score ?? null;
            }
          } else {
            // Create new user entry
            userMap.set(uniqueKey, {
              id: isAnonymous ? "anonymous" : session.enduser?.id || 0,
              firstName: session.enduser?.firstname || "",
              lastName: session.enduser?.lastname || "",
              email: session.enduser?.email || "",
              username: session.user_name || session.enduser?.username || "",
              sessionCount: 1,
              lastSessionDate: sessionDate,
              lastSessionScore: session.score ?? null,
              avatarUrl: session.enduser?.source_avatar?.s3_url,
              isAnonymous,
            });
          }
        });
      });
    });

    return Array.from(userMap.values());
  }, [sessionsByProjectGroup, dateRange]);

  // Filter and sort
  const filteredAndSortedData = useMemo(() => {
    let result = apprenantsList;

    // Search filter
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter(
        (a) =>
          a.firstName.toLowerCase().includes(search) ||
          a.lastName.toLowerCase().includes(search) ||
          a.email.toLowerCase().includes(search) ||
          a.username.toLowerCase().includes(search),
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      const direction = sortDirection === "asc" ? 1 : -1;

      switch (sortColumn) {
        case "sessions":
          return (a.sessionCount - b.sessionCount) * direction;
        case "firstName":
          return a.firstName.localeCompare(b.firstName) * direction;
        case "lastName":
          return a.lastName.localeCompare(b.lastName) * direction;
        case "email":
          return a.email.localeCompare(b.email) * direction;
        case "lastDate":
          if (!a.lastSessionDate) return 1;
          if (!b.lastSessionDate) return -1;
          return (a.lastSessionDate.getTime() - b.lastSessionDate.getTime()) * direction;
        case "lastScore":
          return ((a.lastSessionScore || 0) - (b.lastSessionScore || 0)) * direction;
        default:
          return 0;
      }
    });

    return result;
  }, [apprenantsList, searchValue, sortColumn, sortDirection]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchValue, sortColumn, sortDirection, dateRange]);

  // Paginate the data
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredAndSortedData.slice(start, start + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const handleSelectAll = () => {
    if (selectedApprenants.size === filteredAndSortedData.length) {
      setSelectedApprenants(new Set());
    } else {
      setSelectedApprenants(new Set(filteredAndSortedData.map((a) => a.id)));
    }
  };

  const handleSelectApprenant = (id: number | string) => {
    const newSelected = new Set(selectedApprenants);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedApprenants(newSelected);
  };

  const handleApprenantClick = (apprenant: ApprenantData) => {
    if (apprenant.isAnonymous) {
      navigate("/statistics/apprenants/anonymous");
    } else {
      navigate(`/statistics/apprenants/${apprenant.id}`);
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return sortDirection === "asc" ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 text-green-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 text-green-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const getScoreColor = (score: number | null): string => {
    if (score === null) return "text-gray-400";
    if (score >= 16) return "text-green-600";
    if (score >= 10) return "text-yellow-600";
    return "text-red-600";
  };

  const isSimplifiedAccount = (email: string): boolean => {
    return email?.endsWith("@sneakysneaky.ua") ?? false;
  };

  const renderEmailCell = (apprenant: ApprenantData) => {
    if (apprenant.isAnonymous) {
      return "-";
    }
    if (!apprenant.email) {
      return "-";
    }
    if (isSimplifiedAccount(apprenant.email)) {
      const code = parseSimplifiedUserIdentifier(apprenant.email);
      return (
        <div className="flex items-center gap-2">
          <span className="font-mono">{code}</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
            {t("statsNew.apprenants.simplified")}
          </span>
        </div>
      );
    }
    return apprenant.email;
  };

  const handleExport = async () => {
    const localeCode = i18n.language === "fr" ? "fr" : "en";
    // Export selected or all learners
    const dataToExport =
      selectedApprenants.size > 0
        ? filteredAndSortedData.filter((a) => selectedApprenants.has(a.id))
        : filteredAndSortedData;

    await exportLearnersToCsv(
      dataToExport.map((a) => ({
        id: a.id,
        firstName: a.firstName,
        lastName: a.lastName,
        email: a.email,
        sessionCount: a.sessionCount,
        lastSessionDate: a.lastSessionDate,
        lastScore: a.lastSessionScore,
        isAnonymous: a.isAnonymous,
      })),
      {
        filename: "apprenants",
        locale: localeCode,
      },
    );
  };

  // Show skeleton during initial load
  if (showSkeleton) {
    return (
      <div className="space-y-6">
        <SearchAndSort
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t("statsNew.apprenants.searchPlaceholder")}
        />
        <SkeletonTable rows={8} columns={6} showCheckbox showAvatar />
      </div>
    );
  }

  // Render empty state based on context
  const renderEmptyState = () => {
    if (searchValue) {
      return (
        <EmptyState
          icon="search"
          title={t("statsNew.emptyState.noSearchResults")}
          description={t("statsNew.emptyState.noSearchResultsDescription")}
        />
      );
    }

    if (dateRange.startDate || dateRange.endDate) {
      return (
        <EmptyState
          icon="calendar"
          title={t("statsNew.emptyState.noApprenants")}
          description={t("statsNew.emptyState.noApprenantsDescription")}
          action={{
            label: t("statsNew.emptyState.viewAll"),
            onClick: handleClearDateRange,
          }}
        />
      );
    }

    return (
      <EmptyState
        icon="users"
        title={t("statsNew.emptyState.noApprenants")}
        description={t("statsNew.emptyState.noApprenantsDescription")}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search and Export */}
      <div className="flex-shrink-0 flex items-center justify-between gap-4 mb-4">
        <SearchAndSort
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t("statsNew.apprenants.searchPlaceholder")}
        />
        <ExportButton onExport={handleExport} disabled={filteredAndSortedData.length === 0} />
      </div>

      {/* Table */}
      {filteredAndSortedData.length === 0 ? (
        renderEmptyState()
      ) : (
        <div className="flex-1 overflow-y-auto min-h-0 bg-white rounded-xl border border-gray-200">
          <table className="stats-table">
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    checked={selectedApprenants.size === filteredAndSortedData.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                  />
                </th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("sessions")}>
                  <div className="flex items-center gap-1">
                    {t("statsNew.apprenants.sessions")}
                    {getSortIcon("sessions")}
                  </div>
                </th>
                <th className="w-10"></th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("firstName")}>
                  <div className="flex items-center gap-1">
                    {t("statsNew.apprenants.firstName")}
                    {getSortIcon("firstName")}
                  </div>
                </th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("lastName")}>
                  <div className="flex items-center gap-1">
                    {t("statsNew.apprenants.lastName")}
                    {getSortIcon("lastName")}
                  </div>
                </th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("email")}>
                  <div className="flex items-center gap-1">
                    {t("statsNew.apprenants.email")}
                    {getSortIcon("email")}
                  </div>
                </th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("lastDate")}>
                  <div className="flex items-center gap-1">
                    {t("statsNew.apprenants.lastSession")}
                    {getSortIcon("lastDate")}
                  </div>
                </th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("lastScore")}>
                  <div className="flex items-center gap-1">
                    {t("statsNew.apprenants.lastScore")}
                    {getSortIcon("lastScore")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((apprenant) => (
                <tr
                  key={apprenant.isAnonymous ? `anon_${apprenant.id}` : apprenant.id}
                  className="clickable"
                  onClick={() => handleApprenantClick(apprenant)}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedApprenants.has(apprenant.id)}
                      onChange={() => handleSelectApprenant(apprenant.id)}
                      className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    />
                  </td>
                  <td>
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      {apprenant.sessionCount}
                    </span>
                  </td>
                  <td>
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {apprenant.avatarUrl ? (
                        <img
                          src={apprenant.avatarUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      )}
                    </div>
                  </td>
                  <td className="font-medium">
                    {apprenant.isAnonymous ? (
                      <span className="text-gray-400 italic">
                        {apprenant.username || t("statsNew.apprenants.anonymous")}
                      </span>
                    ) : (
                      apprenant.firstName || "-"
                    )}
                  </td>
                  <td>{apprenant.isAnonymous ? "-" : apprenant.lastName || "-"}</td>
                  <td className="text-gray-500">{renderEmailCell(apprenant)}</td>
                  <td className="text-gray-500">
                    {apprenant.lastSessionDate
                      ? format(apprenant.lastSessionDate, "dd MMM yyyy", { locale })
                      : "-"}
                  </td>
                  <td>
                    <span className={`font-semibold ${getScoreColor(apprenant.lastSessionScore)}`}>
                      {apprenant.lastSessionScore !== null
                        ? apprenant.lastSessionScore.toFixed(1)
                        : "-"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {filteredAndSortedData.length > 0 && (
        <div className="flex-shrink-0 mt-auto">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredAndSortedData.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Selected count */}
      {selectedApprenants.size > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-4">
          <span>
            {selectedApprenants.size} {t("statsNew.apprenants.selected")}
          </span>
          <button
            onClick={() => setSelectedApprenants(new Set())}
            className="text-gray-400 hover:text-white"
          >
            {t("general.cancel")}
          </button>
        </div>
      )}
    </div>
  );
};

export default ApprenantsListView;
